import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import StylishDynamicTabs from '../../../../components/StylishDynamicTabs';
import AppCard from '../../../../components/cards/AppCard';
import {
  getPipelineCount,
  getShortlistedCount,
  getSubmittedCandidates,
} from '../../../../services/dropdownApi';
import { getWorkflowByEntity } from '../../../../services/workflowApi';
import SourcingTab from './SourcingTab';
import WorkflowStageTab from './WorkflowStageTab';

const PIPELINE_ACTIONS = [
  { key: 'quick-submit', label: 'Quick Submit', route: '/popup/quick-submit', maxSelect: 1 },
  { key: 'shortlist',    label: 'Shortlist',    route: '/popup/quick-submit', popupState: { defaultStage: 'Shortlisted' } },
  { key: 'tag-to-job',   label: 'Tag to Jobs',  route: '/popup/tag-to-job' },
  { key: 'mail',         label: 'Send Email',   route: '/popup/mail' },
];

const SHORTLISTED_ACTIONS = [
  { key: 'quick-submit',          label: 'Quick Submit',          route: '/popup/quick-submit',          maxSelect: 1 },
  { key: 'tag-to-job',            label: 'Tag to Jobs',           route: '/popup/tag-to-job' },
  { key: 'mail',                  label: 'Send Email',            route: '/popup/mail' },
  { key: 'remove-from-shortlist', label: 'Remove from Shortlist', route: '/popup/remove-from-shortlist', danger: true },
];

const SUBMITTED_ACTIONS = [
  { key: 'mail', label: 'Send Email', route: '/popup/mail' },
];

function getWorkflowIdFromWorkflow(workflow) {
  return workflow?.workflowId ?? workflow?._id ?? workflow?.id ?? null;
}

export default function CandidatesTab({ jobId, onDescriptionSaved }) {
  // The active sub-tab is driven by the `?subTab=` URL param so it can be
  // deep-linked directly (e.g. /jobs/:id?tab=candidates&subTab=pipeline).
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSubTab = searchParams.get('subTab') || 'source';
  const setActiveSubTab = (key) => {
    const next = new URLSearchParams(searchParams);
    next.set('subTab', key);
    setSearchParams(next, { replace: true });
  };
  const [pipelineCount, setPipelineCount]     = useState(null);
  const [shortlistedCount, setShortlistedCount] = useState(null);
  const [submittedCount, setSubmittedCount] = useState(null);
  const [workflowId, setWorkflowId] = useState(null);

  const fetchCounts = () => {
    if (!jobId) return;
    getPipelineCount({ jobId }).then(setPipelineCount).catch(() => {});
    getShortlistedCount({ jobId }).then(setShortlistedCount).catch(() => {});
    getSubmittedCandidates(jobId)
      .then((data) => setSubmittedCount(Array.isArray(data) ? data.length : 0))
      .catch(() => {});
  };

  useEffect(() => { fetchCounts(); }, [jobId]);

  // jobId is the entityId for the job's HiringPipeline workflow — resolve the
  // workflowId once so the Pipeline/Shortlisted/Submitted tabs can each pull
  // their stage data from it.
  useEffect(() => {
    if (!jobId) return undefined;

    let cancelled = false;
    getWorkflowByEntity(jobId)
      .then((workflow) => {
        if (cancelled) return;
        setWorkflowId(getWorkflowIdFromWorkflow(workflow));
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[CandidatesTab] getWorkflowByEntity failed:', jobId, err);
        setWorkflowId(null);
      });

    return () => {
      cancelled = true;
    };
  }, [jobId]);

  const tabs = [
    { key: 'source',      label: 'Source' },
    { key: 'pipeline',    label: 'Pipeline',    count: pipelineCount },
    { key: 'shortlisted', label: 'Shortlisted', count: shortlistedCount },
    { key: 'submitted',   label: 'Submitted',   count: submittedCount },
  ];

  return (
    <div className="candidates-tab-wrapper">
      <AppCard>
        <StylishDynamicTabs
          tabs={tabs}
          activeKey={activeSubTab}
          onChange={setActiveSubTab}
        />
      </AppCard>

      {activeSubTab === 'source' && (
        <SourcingTab jobId={jobId} onActionComplete={fetchCounts} onDescriptionSaved={onDescriptionSaved} />
      )}
      {activeSubTab === 'pipeline' && (
        <WorkflowStageTab
          workflowId={workflowId}
          stageName="Pipeline"
          actions={PIPELINE_ACTIONS}
          onCountChange={setPipelineCount}
        />
      )}
      {activeSubTab === 'shortlisted' && (
        <WorkflowStageTab
          workflowId={workflowId}
          stageName="Shortlisted"
          actions={SHORTLISTED_ACTIONS}
          onCountChange={setShortlistedCount}
        />
      )}
      {activeSubTab === 'submitted' && (
        <WorkflowStageTab
          workflowId={workflowId}
          stageName="Submitted"
          actions={SUBMITTED_ACTIONS}
          onCountChange={setSubmittedCount}
        />
      )}
    </div>
  );
}
